import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs/internal/Observable';
import {DealDashboardListDto, DealDetailDto, DealDetailStaticDto, DealDetailPricingDto, ShareDealView} from '../models/DealsModels';
import {shareReplay} from 'rxjs/internal/operators/shareReplay';
import {defer} from 'rxjs/internal/observable/defer';
import {catchError, retry, tap} from 'rxjs/operators';
import {timer} from 'rxjs/internal/observable/timer';
import {throwError} from 'rxjs/internal/observable/throwError';
import {of} from 'rxjs/internal/observable/of';
import {ToastrService} from 'ngx-toastr';

const DEALS_URL = '/api/deals';
const DEAL_TYPES_URL = '/api/deals';

// 1 hour
const DEAL_TTL_MS = 60 * 60 * 1000;

interface CacheEntry<T> {
    data: T;
    expiresAt: number;
}

//todo create a caching service that will work with any entity type. Extract these caching methods
@Injectable({
    providedIn: 'root'
})
export class DealsService {
    private dealsCache = new Map<string, Observable<DealDashboardListDto>>();
    private dealStaticCache = new Map<string, Observable<DealDetailStaticDto>>();
    private dealPricingCache = new Map<string, Observable<DealDetailPricingDto>>();

    constructor(
        private http: HttpClient,
        private toastr: ToastrService,
    ) {}

    // ---------- LIST OF DEALS WITH PERSISTENT CACHE ----------
    getDeals(forceRefresh?: boolean): Observable<DealDashboardListDto> {
        const storageKey = 'deals-list';

        const cachedOrStored = this.getFromCacheOrStorage<DealDashboardListDto>(
            this.dealsCache,
            storageKey,
            forceRefresh
        );
        if (cachedOrStored) {
            return cachedOrStored;
        }

        // 3) fetch from API
        const obs$ = defer(() =>
            this.http.get<DealDashboardListDto>(`${DEALS_URL}/me`, { withCredentials: true }).pipe(
                retry({
                    count: 2,
                    resetOnSuccess: true,
                    delay: (err, retryCount) => {
                        const status = err?.status;
                        const transient = status === 0 || (status >= 500 && status < 600);
                        if (!transient) {
                            return throwError(() => err);
                        }
                        const ms = Math.min(500 * Math.pow(2, retryCount - 1), 4000);
                        return timer(ms);
                    },
                }),
                tap(data => {
                    this.saveToStorage<DealDashboardListDto>(storageKey, data);
                }),
                tap({ error: () => this.dealsCache.delete(storageKey) }),
                shareReplay({
                    bufferSize: 1,
                    refCount: true,
                    windowTime: DEAL_TTL_MS
                }),
                catchError(err => {
                    this.dealsCache.delete(storageKey);
                    this.toastr.error('Please refresh', 'Failed fetching deals');
                    return throwError(() => err);
                })
            )
        );

        this.dealsCache.set(storageKey, obs$);
        return obs$;
    }

    // ---------- STATIC DEAL DATA (FAST) ----------
    getDealStatic(dealId: number, forceRefresh = false): Observable<DealDetailStaticDto> {
        const storageKey = `deal-static-${dealId}`;

        const cachedOrStored = this.getFromCacheOrStorage<DealDetailStaticDto>(
            this.dealStaticCache,
            storageKey,
            forceRefresh
        );
        if (cachedOrStored) {
            return cachedOrStored;
        }

        const obs$ = defer(() =>
            this.http.get<DealDetailStaticDto>(`${DEAL_TYPES_URL}/${dealId}/static`, {
                withCredentials: true
            }).pipe(
                tap(deal => {
                    this.saveToStorage<DealDetailStaticDto>(storageKey, deal);
                }),
                tap({ error: () => this.dealStaticCache.delete(storageKey) }),
                shareReplay({
                    bufferSize: 1,
                    refCount: true,
                    windowTime: DEAL_TTL_MS
                })
            )
        );

        this.dealStaticCache.set(storageKey, obs$);
        return obs$;
    }

    // ---------- PRICING DATA (MAY BE SLOW) ----------
    getDealPricing(dealId: number, forceRefresh = false): Observable<DealDetailPricingDto> {
        const storageKey = `deal-pricing-${dealId}`;

        const cachedOrStored = this.getFromCacheOrStorage<DealDetailPricingDto>(
            this.dealPricingCache,
            storageKey,
            forceRefresh
        );
        if (cachedOrStored) {
            return cachedOrStored;
        }

        const obs$ = defer(() =>
            this.http.get<DealDetailPricingDto>(`${DEAL_TYPES_URL}/${dealId}/pricing`, {
                withCredentials: true
            }).pipe(
                tap(pricing => {
                    this.saveToStorage<DealDetailPricingDto>(storageKey, pricing);
                }),
                tap({ error: () => this.dealPricingCache.delete(storageKey) }),
                shareReplay({
                    bufferSize: 1,
                    refCount: true,
                    windowTime: DEAL_TTL_MS
                })
            )
        );

        this.dealPricingCache.set(storageKey, obs$);
        return obs$;
    }


    // ---------- STORAGE HELPERS  ----------

    private readFromStorage<T>(key: string): T | null {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;

            const entry = JSON.parse(raw) as CacheEntry<T>;
            if (!entry || !entry.expiresAt || !entry.data) {
                localStorage.removeItem(key);
                return null;
            }

            if (Date.now() > entry.expiresAt) {
                localStorage.removeItem(key);
                return null;
            }

            return entry.data;
        } catch {
            localStorage.removeItem(key);
            return null;
        }
    }

    private saveToStorage<T>(key: string, data: T): void {
        const entry: CacheEntry<T> = {
            data,
            expiresAt: Date.now() + DEAL_TTL_MS,
        };

        try {
            localStorage.setItem(key, JSON.stringify(entry));
        } catch {}
    }
    private getFromCacheOrStorage<T>(
        cache: Map<string, Observable<T>>,
        storageKey: string,
        forceRefresh?: boolean
    ): Observable<T> | null {
        if (!forceRefresh) {
            // 1) in-memory
            const cached = cache.get(storageKey);
            if (cached) {
                return cached;
            }

            // 2) localStorage
            const stored = this.readFromStorage<T>(storageKey);
            if (stored) {
                const obs$ = of(stored).pipe(
                    shareReplay({
                        bufferSize: 1,
                        refCount: true,
                        windowTime: DEAL_TTL_MS
                    })
                );
                cache.set(storageKey, obs$);
                return obs$;
            }
        }

        return null;
    }

    // Nuke everything
    clearAllDealCaches(): void {
        this.dealsCache.clear();
        this.dealStaticCache.clear();
        this.dealPricingCache.clear();

        Object.keys(localStorage)
            .filter(k => k.startsWith('deal-') || k.startsWith('deals-list'))
            .forEach(k => localStorage.removeItem(k));
    }


    getSharedDeal(dealId: number): Observable<ShareDealView> {
        return this.http.get<ShareDealView>(`${DEALS_URL}/${dealId}/shared`)
    }

}
